import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { graphql, Link } from 'gatsby'
import Img from 'gatsby-image'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'

import Layout, { OverviewWrap } from 'src/components/layout/layout'
import CustomRow from 'src/components/core/row'

// -----------------------------------------------------------------------------
// Editions
// -----------------------------------------------------------------------------

const EDITIONS = {
  plumber: {
    title: 'Apprentice Plumber',
    project: 'plumbersapprentice',
    variants: {
      '36cm': {
        file: '1t',
        qualifier: '36cm',
        price: '£3,950',
      },
    },
  },

  mcindoe: {
    title: 'Archibald McIndoe',
    project: 'mcindoe',
    variants: {
      '30cm': {
        file: '1',
        qualifier: '30cm',
        price: '£3,950',
      },
    },
  },

  car: {
    title: 'Caribbean',
    project: 'caribbeanmonument',
    variants: {
      airman: {
        file: 'air',
        qualifier: 'airman 13cm',
        price: '£985',
      },

      ats: {
        file: 'ATS',
        qualifier: 'ATS 13cm',
        price: '£985',
      },

      sailor: {
        file: 'sailor',
        qualifier: 'sailor 13cm',
        price: '£985',
      },

      soldier: {
        file: 'soldierc',
        qualifier: 'soldier 13cm',
        price: '£985',
      },
    },
  },

  dickens: {
    title: 'Charles Dickens',
    project: 'charlesdickens',
    variants: {
      '11cm': {
        file: '11cm',
        qualifier: '11cm',
        price: '£1,950',
      },

      '32cm': {
        file: '32c',
        qualifier: '32cm',
        price: '£6,400',
      },

      bust: {
        file: '18t',
        qualifier: 'bust 18cm',
        price: '£2,150',
      },
    },
  },

  orwell: {
    title: 'George Orwell',
    project: 'orwell',
    variants: {
      '48cm': {
        file: '48cm',
        qualifier: '48cm',
        price: '£6,800',
      },

      brown: {
        file: '16cm-brownc',
        qualifier: 'brown 16cm',
        price: '£1,500',
      },

      red: {
        file: '16cm-red',
        qualifier: 'red 16cm',
        price: '£1,500',
      },
    },
  },

  austen: {
    title: 'Jane Austen',
    project: 'janeausten',
    variants: {
      '16cm': {
        file: '16cm',
        qualifier: '16cm',
        price: '£1,875',
      },

      '36cm': {
        file: '36cm',
        qualifier: '36cm',
        price: '£4,650',
      },

      '55cm': {
        file: '55cm',
        qualifier: '55cm',
        price: '£8,800',
      },
    },
  },

  betjeman: {
    title: 'John Betjeman',
    project: 'betjeman',
    variants: {
      '15cm': {
        file: '15cm',
        qualifier: '15cm',
        price: '£1,650',
      },

      '30cm': {
        file: '30cm',
        qualifier: '30cm',
        price: '£4,250',
      },
    },
  },

  larkin: {
    title: 'Philip Larkin',
    project: 'larkin',
    variants: {
      '15cm': {
        file: '15cm',
        qualifier: '15cm',
        price: '£1,650',
      },

      '39cm': {
        file: '39cm',
        qualifier: '39cm',
        price: '£4,800',
      },
    },
  },

  radcliffe: {
    title: 'John Radcliffe',
    project: 'radcliffe',
    variants: {
      '27cm': {
        file: '27t',
        qualifier: '27cm',
        price: '£3,600',
      },

      '44cm': {
        file: '44t',
        qualifier: '44cm',
        price: '£5,700',
      },
    },
  },

  seacole: {
    title: 'Mary Seacole',
    project: 'seacole',
    variants: {
      '15cm': {
        file: '15cm',
        qualifier: '15cm',
        price: '£1,700',
      },

      brooch: {
        file: 'brooch4cm',
        qualifier: 'brooch 4cm',
        pricePrefix: 'Sterling Silver',
        price: '£995',
      },

      pendant: {
        file: 'pendant4cm',
        qualifier: 'pendant 4cm',
        pricePrefix: 'Sterling Silver',
        price: '£995',
      },
    },
  },

  mask: {
    title: 'Edward Heath',
    project: 'portraits',
    variants: {
      '8cm': {
        file: '1t',
        qualifier: '8cm',
        price: '£1,100',
      },
    },
  },

  baldwin: {
    title: 'Stanley Baldwin',
    project: 'baldwin',
    variants: {
      '17cm': {
        file: '17t',
        qualifier: '17cm',
        price: '£1,500',
      },

      '40cm': {
        file: '40t',
        qualifier: '40cm',
        price: '£4,950',
      },
    },
  },

  wos: {
    title: 'Women of Steel',
    project: 'womenofsteel',
    variants: {
      '15cm': {
        file: '15cm',
        qualifier: '15cm',
        price: '£2,150',
      },

      '32cm': {
        file: '32c',
        qualifier: '32cm',
        price: '£4,800',
      },
    },
  },
}

// -----------------------------------------------------------------------------
// Explicit layout from the mockup
//
// Each string is "folder.variant".
// -----------------------------------------------------------------------------

const EDITION_ROWS = [
  [
    'orwell.48cm',
    'orwell.brown',
    'orwell.red',
  ],

  [
    'austen.16cm',
    'austen.36cm',
    'austen.55cm',
  ],

  [
    'betjeman.15cm',
    'betjeman.30cm',
    'larkin.15cm',
    'larkin.39cm',
  ],

  [
    'dickens.11cm',
    'dickens.32cm',
    'dickens.bust',
  ],

  [
    'seacole.15cm',
    'seacole.brooch',
    'seacole.pendant',
  ],

  [
    'wos.15cm',
    'wos.32cm',
    'plumber.36cm',
    'mcindoe.30cm',
  ],

  [
    'baldwin.17cm',
    'baldwin.40cm',
    'mask.8cm',
  ],

  [
    'car.airman',
    'car.ats',
    'car.sailor',
    'car.soldier',
  ],

  [
    'radcliffe.27cm',
    'radcliffe.44cm',
  ],
]

// -----------------------------------------------------------------------------
// Page layout
// -----------------------------------------------------------------------------

const Page = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding-bottom: 5rem;
`

const PageTitle = styled.h1`
  margin: 0 0 2rem;

  font-size: 2.2rem;
  line-height: 1.1;
  font-weight: normal;

  @media (max-width: 767px) {
    font-size: 1.8rem;
  }
`

const Introduction = styled.div`
  max-width: 860px;
  margin-bottom: 5rem;

  p {
    margin: 0 0 1.2rem;

    font-size: 1rem;
    line-height: 1.65;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`

// -----------------------------------------------------------------------------
// Catalogue
// -----------------------------------------------------------------------------

const Catalogue = styled.div`
  display: flex;
  flex-direction: column;

  gap: 4rem;
`

const CatalogueRow = styled.div`
  display: grid;

  grid-template-columns: repeat(
    ${({ $count }) => $count},
    240px
  );

  justify-content: center;
  align-items: start;

  column-gap: 2rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(
      ${({ $count }) => Math.min($count, 3)},
      220px
    );

    row-gap: 2.5rem;
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(
      2,
      minmax(0, 1fr)
    );

    gap: 2.5rem 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const EditionCard = styled.figure`
  width: 100%;
  min-width: 0;

  margin: 0;
`

const ImageFrame = styled.button`
  position: relative;

  display: block;

  width: 100%;
  aspect-ratio: 1 / 1;

  padding: 0;
  border: 0;

  overflow: hidden;

  background: transparent;

  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }

  &:disabled {
    cursor: default;
  }
`

const MissingImage = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  border: 1px solid rgba(0, 0, 0, 0.12);

  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.4);
`

const Caption = styled.figcaption`
  margin-top: 0.75rem;

  font-size: 0.95rem;
  line-height: 1.4;
`

const ProductNameLine = styled.div`
  margin-bottom: 0.2rem;
`

const SculptureLink = styled(Link)`
  color: inherit;

  font-family: "Lato", sans-serif !important;
  font-weight: 600;

  text-decoration: none;

  &:hover {
    opacity: 0.6;
  }
`

const Qualifier = styled.span`
  font-family: "Lato", sans-serif !important;
  font-weight: 600;
`

const PricePrefix = styled.div`
  font-weight: normal;
`

const Price = styled.div`
  font-weight: normal;
`

// -----------------------------------------------------------------------------
// Lightbox
// -----------------------------------------------------------------------------

const LightboxBackdrop = styled.div`
  position: fixed;
  inset: 0;

  z-index: 10000;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 2rem;

  background: rgba(0, 0, 0, 0.88);

  cursor: zoom-out;

  @media (max-width: 767px) {
    padding: 1rem;
  }
`

const LightboxContent = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  max-width: 100%;
  max-height: 100%;

  cursor: default;
`

const LightboxImage = styled.img`
  display: block;

  width: auto;
  height: auto;

  max-width: calc(100vw - 4rem);
  max-height: calc(100vh - 4rem);

  object-fit: contain;

  box-shadow: 0 18px 70px rgba(0, 0, 0, 0.45);

  @media (max-width: 767px) {
    max-width: calc(100vw - 2rem);
    max-height: calc(100vh - 2rem);
  }
`

const LightboxClose = styled.button`
  position: absolute;

  top: 0;
  right: 0;

  z-index: 1;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 44px;
  height: 44px;

  padding: 0;
  border: 0;

  background: rgba(0, 0, 0, 0.45);

  color: white;

  font-size: 2rem;
  line-height: 1;

  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`

// -----------------------------------------------------------------------------
// Lightbox component
//
// Defined in this page file, but rendered directly under document.body.
// This avoids any stacking context created by Layout or its ancestors.
// -----------------------------------------------------------------------------

const Lightbox = ({ edition, onClose }) => {
  useEffect(() => {
    if (!edition) {
      return undefined
    }

    const previousOverflow =
      document.body.style.overflow

    document.body.style.overflow = 'hidden'

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown
    )

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      )

      document.body.style.overflow =
        previousOverflow
    }
  }, [edition, onClose])

  if (
    !edition?.lightboxImage ||
    typeof document === 'undefined'
  ) {
    return null
  }

  return ReactDOM.createPortal(
    <LightboxBackdrop
      role="dialog"
      aria-modal="true"
      aria-label={`${edition.title} ${edition.qualifier}`}
      onClick={onClose}
    >
      <LightboxContent
        onClick={event =>
          event.stopPropagation()
        }
      >
        <LightboxClose
          type="button"
          aria-label="Close image"
          onClick={onClose}
        >
          ×
        </LightboxClose>

        <LightboxImage
          src={edition.lightboxImage.src}
          srcSet={edition.lightboxImage.srcSet}
          sizes="100vw"
          alt={`${edition.title} ${edition.qualifier}`}
        />
      </LightboxContent>
    </LightboxBackdrop>,
    document.body
  )
}

// -----------------------------------------------------------------------------
// Data
// -----------------------------------------------------------------------------

function getFolderName(relativeDirectory) {
  const parts = relativeDirectory.split('/')

  return parts[parts.length - 1]
}

function buildFileIndex(files) {
  return files.reduce((index, file) => {
    const folder = getFolderName(
      file.relativeDirectory
    )

    if (!index[folder]) {
      index[folder] = {}
    }

    index[folder][
      file.name.toLowerCase()
    ] = file

    return index
  }, {})
}

function buildEditionIndex(files) {
  const fileIndex = buildFileIndex(files)

  return Object.entries(EDITIONS).reduce(
    (result, [folder, edition]) => {
      result[folder] = {}

      Object.entries(
        edition.variants
      ).forEach(
        ([variantKey, variant]) => {
          const file = variant.file
            ? fileIndex[folder]?.[
                variant.file.toLowerCase()
              ]
            : null

          result[folder][variantKey] = {
            key: variantKey,

            folder,

            title: edition.title,

            project: edition.project,

            ...variant,

            image:
              file?.childImageSharp?.fixed ||
              null,

            lightboxImage:
              file?.childImageSharp?.fluid ||
              null,
          }
        }
      )

      return result
    },
    {}
  )
}

function getEdition(index, reference) {
  const [folder, variant] =
    reference.split('.')

  return (
    index[folder]?.[variant] || null
  )
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

const EditionsPage = ({ data }) => {
  const editionIndex = buildEditionIndex(
    data.allFile.nodes
  )

  const [
    lightboxEdition,
    setLightboxEdition,
  ] = useState(null)

  const closeLightbox = () => {
    setLightboxEdition(null)
  }

  return (
    <>
      <Layout>
        <Helmet>
          <meta charSet="utf-8" />

          <title>
            Sculpture Editions — Martin Jennings
          </title>

          <meta
            name="description"
            content="Limited sculpture editions and maquettes by Martin Jennings."
          />
        </Helmet>

        <CustomRow>
          <OverviewWrap>
            <Page>
              <PageTitle>
                Sculpture Editions
              </PageTitle>

              <Introduction>
                <p>
                  Maquettes of the sculptures are
                  for sale in limited editions. All
                  works are cast in bronze unless
                  otherwise described and are
                  available in different sizes. For
                  orders and enquiries please
                  contact{' '}
                  <a href="mailto:mj@martinjennings.com">
                    mj@martinjennings.com
                  </a>
                  .
                </p>

                <p>
                  All maquettes are numbered,
                  signed by the artist and stamped
                  by the foundry.
                </p>

                <p>
                  50% payment in advance guarantees
                  purchase of the next cast in the
                  edition. The remaining 50% is due
                  when the sculpture is ready for
                  dispatch. VAT will be added to UK
                  sales.
                </p>

                <p>
                  Sculptures not in stock are cast
                  to order. Depending on the
                  foundry schedule delivery can
                  take from 12 to 20 weeks. The
                  cost of crating (for larger
                  sculptures) and dispatch will be added to the
                  charge.
                </p>
              </Introduction>

              <Catalogue>
                {EDITION_ROWS.map(
                  (row, rowIndex) => {
                    const editions = row
                      .map(reference =>
                        getEdition(
                          editionIndex,
                          reference
                        )
                      )
                      .filter(Boolean)

                    return (
                      <CatalogueRow
                        key={rowIndex}
                        $count={editions.length}
                      >
                        {editions.map(
                          edition => (
                            <EditionCard
                              key={`${edition.folder}-${edition.key}`}
                            >
                              <ImageFrame
                                type="button"
                                disabled={
                                  !edition.lightboxImage
                                }
                                aria-label={`Open ${edition.title} ${edition.qualifier} image`}
                                onClick={() => {
                                  if (
                                    edition.lightboxImage
                                  ) {
                                    setLightboxEdition(
                                      edition
                                    )
                                  }
                                }}
                              >
                                {edition.image ? (
                                  <Img
                                    fixed={
                                      edition.image
                                    }
                                    alt={`${edition.title} ${edition.qualifier}`}
                                    loading="lazy"
                                    fadeIn={false}
                                    style={{
                                      position:
                                        'absolute',

                                      inset: 0,

                                      width:
                                        '100%',

                                      height:
                                        '100%',
                                    }}
                                    imgStyle={{
                                      width:
                                        '100%',

                                      height:
                                        '100%',

                                      objectPosition:
                                        'center',

                                      objectFit:
                                        edition.fit ||
                                        'cover',
                                    }}
                                  />
                                ) : (
                                  <MissingImage>
                                    Image unavailable
                                  </MissingImage>
                                )}
                              </ImageFrame>

                              <Caption>
                                <ProductNameLine>
                                  <SculptureLink
                                    to={`/projects/${edition.project}`}
                                  >
                                    {
                                      edition.title
                                    }
                                  </SculptureLink>

                                  {edition.qualifier && (
                                    <Qualifier>
                                      {' '}
                                      {
                                        edition.qualifier
                                      }
                                    </Qualifier>
                                  )}
                                </ProductNameLine>

                                {edition.pricePrefix && (
                                  <PricePrefix>
                                    {
                                      edition.pricePrefix
                                    }
                                  </PricePrefix>
                                )}

                                <Price>
                                  {
                                    edition.price
                                  }
                                </Price>
                              </Caption>
                            </EditionCard>
                          )
                        )}
                      </CatalogueRow>
                    )
                  }
                )}
              </Catalogue>
            </Page>
          </OverviewWrap>
        </CustomRow>
      </Layout>

      <Lightbox
        edition={lightboxEdition}
        onClose={closeLightbox}
      />
    </>
  )
}

export default EditionsPage

// -----------------------------------------------------------------------------
// Images
//
// fixed:
//   small catalogue thumbnail
//
// fluid:
//   separate enlarged image generated by Sharp.
//   maxWidth prevents the native source file from being served.
// -----------------------------------------------------------------------------

export const query = graphql`
  query EditionsPageQuery {
    allFile(
      filter: {
        relativeDirectory: {
          regex: "/^editions\\\\//"
        }
        extension: {
          regex: "/^(jpg|jpeg|png)$/"
        }
      }
      sort: {
        fields: [
          relativeDirectory
          name
        ]
        order: [ASC, ASC]
      }
    ) {
      nodes {
        base
        name
        relativeDirectory

        childImageSharp {
          fixed(
            width: 240
            quality: 82
          ) {
            ...GatsbyImageSharpFixed_withWebp_noBase64
          }

          fluid(
            maxWidth: 1800
            quality: 88
          ) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
    }
  }
`